import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminAuth from '../components/admin/AdminAuth';
import ContentCalendar from '../components/admin/ContentCalendar';
import BlogDashboard from '../components/admin/BlogDashboard';
import {
  getGeneratedContentWithVideoStatus,
  approveContent,
  rejectContent,
  deleteGeneratedContent,
  archiveContent,
  changeContentStatus,
  callRepurposeContent,
  callPublishContent,
  callEnhanceTweet,
  createTweetDraft,
  approveAndPublishTweet,
  createManualContent,
  callGenerateVoiceover,
  callGenerateAvatarVideo,
  uploadSelfRecording,
  sendToVideoQueue,
  updateVideoQueueItem,
  deleteVideoQueueItem,
  unarchiveVideoQueueItem
} from '../services/contentOpsService';

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_COLORS = {
  draft: 'bg-yellow-600/20 text-yellow-400',
  approved: 'bg-green-600/20 text-green-400',
  published: 'bg-blue-600/20 text-blue-400',
  archived: 'bg-gray-600/20 text-gray-400'
};

const ALL_STATUSES = ['draft', 'published', 'archived'];

const FORMAT_COLORS = {
  blog: 'bg-blue-600/20 text-blue-400',
  tweet: 'bg-cyan-600/20 text-cyan-400',
  yt_script: 'bg-red-600/20 text-red-400',
  yt_script_short: 'bg-orange-600/20 text-orange-400',
  yt_script_long: 'bg-red-600/20 text-red-400',
  ig: 'bg-pink-600/20 text-pink-400',
  tweet_thread: 'bg-teal-600/20 text-teal-400',
  idea_board: 'bg-amber-600/20 text-amber-400',
  chess_clip: 'bg-emerald-600/20 text-emerald-400'
};

const TWEET_STYLES = ['breaking', 'hot_take', 'analysis', 'trivia', 'recap', 'casual', 'viral'];

const VIDEO_STATUS_LABELS = {
  queued: 'Queued',
  processing_upload: 'Processing',
  voice_ready: 'Voice Done',
  generating: 'Generating',
  rendering: 'Rendering',
  rendered: 'Avatar Ready',
  broll_mixed: 'B-Roll Mixed',
  uploaded: 'Published',
  chess_clip: 'Chess Clip'
};

const FORMAT_LABELS = {
  all: 'all',
  idea_board: 'ideas',
  yt_script_short: 'video',
  yt_script_long: 'video',
  yt_script: 'video',
  tweet: 'tweets',
  tweet_thread: 'tweets',
  blog: 'blogs',
  ig: 'ig',
  chess_clip: 'chess clip'
};

// Format filter categories - groups of formats that should appear as one filter
const FORMAT_FILTERS = [
  { key: 'all', label: 'all', formats: null },
  { key: 'idea_board', label: 'ideas', formats: ['idea_board'] },
  { key: 'tweets', label: 'tweets', formats: ['tweet', 'tweet_thread'] },
  { key: 'video', label: 'video', formats: ['yt_script', 'yt_script_long', 'yt_script_short'] },
  { key: 'ig', label: 'ig', formats: ['ig'] },
  { key: 'chess_clips', label: 'chess clips', formats: ['chess_clip'] },
  { key: 'blog', label: 'blogs', formats: ['blog'] },

];

const ContentPipelinePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('draft');
  const [formatFilter, setFormatFilter] = useState(() => {
    return searchParams.get('tab') === 'video' ? 'video' : 'all';
  });
  const [actionLoading, setActionLoading] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const tab = searchParams.get('tab');
    if (tab === 'video') return 'list'; // video tab = list view with video format filter
    return ['list', 'calendar', 'tweet', 'blogs'].includes(tab) ? tab : 'list';
  });
  const [calendarDate, setCalendarDate] = useState(null);

  // Video action state
  const [generatingVoice, setGeneratingVoice] = useState({});
  const [generatingVideo, setGeneratingVideo] = useState({});
  const [uploadingSelf, setUploadingSelf] = useState({});
  const [copiedBrollId, setCopiedBrollId] = useState(null);
  const [sendingToQueue, setSendingToQueue] = useState({});
  const [publishingVideo, setPublishingVideo] = useState({});

  // Sync viewMode with URL params
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'list') {
      setSearchParams({});
      setCalendarDate(null);
    } else {
      setSearchParams({ tab: mode });
    }
  };

  // Compose tweet state
  const [composeText, setComposeText] = useState('');
  const [composeStyle, setComposeStyle] = useState('casual');
  const [enhancing, setEnhancing] = useState(false);
  const [enhancedText, setEnhancedText] = useState('');
  const [composeError, setComposeError] = useState('');

  // New content modal state
  const [showNewContentModal, setShowNewContentModal] = useState(false);
  const [newContentFormat, setNewContentFormat] = useState('blog');
  const [newContentTopic, setNewContentTopic] = useState('');
  const [newContentBody, setNewContentBody] = useState('');
  const [creatingContent, setCreatingContent] = useState(false);

  useEffect(() => { loadContent(); }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError('');
      const items = await getGeneratedContentWithVideoStatus(100);
      setContent(items);
    } catch (err) {
      console.error('ContentPipeline load error:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setActionLoading(id);
      await changeContentStatus(id, newStatus);
      setContent(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
    } catch (err) {
      setError('Failed to change status');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (id) => {
    try {
      setActionLoading(id);
      await callPublishContent(id, ['blog']);
      await loadContent();
    } catch (err) {
      setError('Failed to publish content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRepurpose = async (id, targetFormat) => {
    try {
      setActionLoading(id);
      await callRepurposeContent(id, targetFormat);
      await loadContent();
    } catch (err) {
      setError('Failed to repurpose content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (item) => {
    if (!window.confirm('Move to trash? You can restore it from the archived filter.')) return;
    try {
      setActionLoading(item.id);
      // Chess clips (orphan videos) are in video_queue, not generated_content
      if (item.isOrphanVideo) {
        await updateVideoQueueItem(item.id, { status: 'archived', archivedAt: new Date() });
      } else {
        await archiveContent(item.id);
      }
      setContent(prev => prev.map(c => c.id === item.id ? { ...c, status: 'archived' } : c));
    } catch (err) {
      setError('Failed to archive content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnarchive = async (item) => {
    try {
      setActionLoading(item.id);
      // Chess clips (orphan videos) are in video_queue, not generated_content
      if (item.isOrphanVideo) {
        await unarchiveVideoQueueItem(item.id);
        // Update local state - set status to draft and clear videoStatus
        setContent(prev => prev.map(c => c.id === item.id ? { ...c, status: 'draft', videoStatus: null } : c));
      } else {
        await changeContentStatus(item.id, 'draft');
        setContent(prev => prev.map(c => c.id === item.id ? { ...c, status: 'draft' } : c));
      }
    } catch (err) {
      setError('Failed to restore content');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveAndPost = async (id) => {
    try {
      setActionLoading(id);
      setError('');
      await approveAndPublishTweet(id);
      setContent(prev => prev.map(c => c.id === id ? { ...c, status: 'published' } : c));
    } catch (err) {
      console.error('Approve & post error:', err);
      setError('Failed to post tweet: ' + (err.message || 'unknown error'));
      // Revert status back to draft
      try {
        await changeContentStatus(id, 'draft');
        setContent(prev => prev.map(c => c.id === id ? { ...c, status: 'draft' } : c));
      } catch {}
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnhance = async () => {
    try {
      setEnhancing(true);
      setComposeError('');
      const result = await callEnhanceTweet(composeText, composeStyle);
      setEnhancedText(result.enhancedText || result.text || '');
    } catch (err) {
      setComposeError('Enhancement failed');
    } finally {
      setEnhancing(false);
    }
  };

  const handleCreateDraft = async (text) => {
    try {
      setActionLoading('compose');
      setComposeError('');
      await createTweetDraft(text, composeStyle);
      setComposeText('');
      setEnhancedText('');
      setViewMode('list');
      await loadContent();
    } catch (err) {
      setComposeError('Failed to create draft');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (item) => {
    const isImportant = item.status === 'published';
    const msg = isImportant
      ? 'This content is ' + item.status + '. Consider archiving instead. Delete permanently?'
      : 'Delete this content permanently?';
    if (!window.confirm(msg)) return;
    try {
      // Chess clips (orphan videos) are in video_queue, not generated_content
      if (item.isOrphanVideo) {
        await deleteVideoQueueItem(item.id);
      } else {
        await deleteGeneratedContent(item.id);
      }
      setContent(prev => prev.filter(c => c.id !== item.id));
    } catch (err) {
      setError('Failed to delete content');
    }
  };

  // Video action handlers
  const handleGenerateVoice = async (videoQueueId) => {
    try {
      setGeneratingVoice(prev => ({ ...prev, [videoQueueId]: true }));
      await callGenerateVoiceover(videoQueueId);
      await loadContent();
    } catch (err) {
      console.error('Voiceover generation error:', err);
      setError('Failed to generate voiceover: ' + (err.message || 'unknown error'));
    } finally {
      setGeneratingVoice(prev => ({ ...prev, [videoQueueId]: false }));
    }
  };

  const handleGenerateVideo = async (videoQueueId) => {
    try {
      setGeneratingVideo(prev => ({ ...prev, [videoQueueId]: true }));
      await callGenerateAvatarVideo(videoQueueId);
      await loadContent();
    } catch (err) {
      console.error('Avatar video generation error:', err);
      setError('Failed to generate avatar video: ' + (err.message || 'unknown error'));
    } finally {
      setGeneratingVideo(prev => ({ ...prev, [videoQueueId]: false }));
    }
  };

  const handleSelfRecordingUpload = async (e, videoQueueId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('video/')) {
      setError('Please upload a video file');
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('File too large (max 500MB)');
      return;
    }

    try {
      setUploadingSelf(prev => ({ ...prev, [videoQueueId]: true }));
      await uploadSelfRecording(videoQueueId, file);
      await loadContent();
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload recording: ' + (err.message || 'unknown error'));
    } finally {
      setUploadingSelf(prev => ({ ...prev, [videoQueueId]: false }));
      e.target.value = '';
    }
  };

  const handleCopyBrollCommand = (videoQueueId) => {
    navigator.clipboard.writeText(`/video-pipeline ${videoQueueId} --broll`);
    setCopiedBrollId(videoQueueId);
    setTimeout(() => setCopiedBrollId(null), 2000);
  };

  const handleSendToQueue = async (contentId) => {
    try {
      setSendingToQueue(prev => ({ ...prev, [contentId]: true }));
      await sendToVideoQueue(contentId);
      await loadContent();
    } catch (err) {
      console.error('Send to queue error:', err);
      setError('Failed to send to video queue: ' + (err.message || 'unknown error'));
    } finally {
      setSendingToQueue(prev => ({ ...prev, [contentId]: false }));
    }
  };

  const handlePublishVideo = async (contentId) => {
    try {
      setPublishingVideo(prev => ({ ...prev, [contentId]: true }));
      await callPublishContent(contentId, ['youtube', 'instagram', 'tiktok']);
      await loadContent();
    } catch (err) {
      console.error('Video publish error:', err);
      setError('Failed to publish video: ' + (err.message || 'unknown error'));
    } finally {
      setPublishingVideo(prev => ({ ...prev, [contentId]: false }));
    }
  };

  const handleCreateManualContent = async () => {
    if (!newContentTopic.trim() || !newContentBody.trim()) return;
    try {
      setCreatingContent(true);
      setError('');
      const contentId = await createManualContent(newContentFormat, newContentTopic.trim(), newContentBody.trim());
      setShowNewContentModal(false);
      setNewContentFormat('blog');
      setNewContentTopic('');
      setNewContentBody('');
      await loadContent();
      // Navigate to the new content editor
      navigate(`/admin/content/editor/${contentId}`);
    } catch (err) {
      setError('Failed to create content');
    } finally {
      setCreatingContent(false);
    }
  };

  // Get the formats array for the current filter
  const getFormatsForFilter = (filterKey) => {
    const filterDef = FORMAT_FILTERS.find(f => f.key === filterKey);
    return filterDef?.formats || null;
  };

  const filtered = content.filter(item => {
    // "All" tab excludes archived
    if (statusFilter === 'all' && item.status === 'archived') return false;
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    // Format filter - check against format group
    if (formatFilter !== 'all') {
      const allowedFormats = getFormatsForFilter(formatFilter);
      if (allowedFormats && !allowedFormats.includes(item.format)) return false;
    }
    // Calendar date filter
    if (calendarDate) {
      const ts = item.createdAt;
      if (!ts) return false;
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      if (d.toISOString().slice(0, 10) !== calendarDate) return false;
    }
    return true;
  });

  const nonArchived = content.filter(c => c.status !== 'archived');
  const statuses = ['draft', 'published', 'all', 'archived'];

  // Only show format filters that have content
  const activeFormatFilters = FORMAT_FILTERS.filter(f => {
    if (f.key === 'all') return true;
    return content.some(c => f.formats?.includes(c.format));
  });

  return (
    <AdminAuth>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-400 hover:text-gray-200 transition-colors text-sm"
              >
                &larr;
              </button>
              <button
                onClick={() => {
                  setStatusFilter('draft');
                  setFormatFilter('all');
                  setCalendarDate(null);
                  handleViewModeChange('list');
                }}
                className="text-3xl font-bold text-gray-100 hover:text-blue-400 transition-colors"
              >
                Content
              </button>
              
            </div>
          </div>
          <div className="flex bg-gray-800 rounded-lg p-1 gap-1">
            <button
              onClick={() => handleViewModeChange('blogs')}
              className="px-3 py-1.5 rounded-md text-sm transition-colors text-gray-400 hover:text-gray-200 hover:bg-gray-700"
            >
              Published Blogs
            </button>
            <button
              onClick={() => setShowNewContentModal(true)}
              className="px-3 py-1.5 rounded-md text-sm transition-colors text-gray-400 hover:text-gray-200 hover:bg-gray-700"
            >
              + New Content
            </button>
            <button
              onClick={() => handleViewModeChange('tweet')}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'tweet'
                  ? 'bg-gray-700 text-gray-100'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              }`}
            >
              + New Tweet
            </button>
          </div>
        </div>

        {/* Tweet Compose View */}
        {viewMode === 'tweet' && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
            <div className="relative">
              <textarea
                value={composeText}
                onChange={(e) => setComposeText(e.target.value.slice(0, 280))}
                placeholder="Write a tweet..."
                rows={4}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
              />
              <span className={`absolute bottom-2 right-2 text-xs ${composeText.length > 260 ? 'text-red-400' : 'text-gray-500'}`}>
                {composeText.length}/280
              </span>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <select
                value={composeStyle}
                onChange={(e) => setComposeStyle(e.target.value)}
                className="bg-gray-900 border border-gray-600 rounded-lg px-2 py-1.5 text-gray-200 text-xs focus:ring-1 focus:ring-cyan-500"
              >
                {TWEET_STYLES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={handleEnhance}
                disabled={!composeText.trim() || enhancing}
                className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50"
              >
                {enhancing ? 'Enhancing...' : 'AI Enhance'}
              </button>
              <button
                onClick={() => handleCreateDraft(composeText)}
                disabled={!composeText.trim() || composeText.length < 10 || actionLoading === 'compose'}
                className="bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-400 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50 ml-auto"
              >
                {actionLoading === 'compose' ? 'Creating...' : 'Create Draft'}
              </button>
            </div>
            {enhancedText && (
              <div className="bg-gray-900 border border-purple-600/30 rounded-lg p-3 space-y-2">
                <p className="text-xs text-purple-400 font-medium">Enhanced version:</p>
                <p className="text-sm text-gray-200">{enhancedText}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { handleCreateDraft(enhancedText); }}
                    disabled={actionLoading === 'compose'}
                    className="bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-1 rounded text-xs transition-colors disabled:opacity-50"
                  >
                    Use Enhanced
                  </button>
                  <button
                    onClick={handleEnhance}
                    disabled={enhancing}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded text-xs transition-colors disabled:opacity-50"
                  >
                    Retry
                  </button>
                  <button
                    onClick={() => setEnhancedText('')}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded text-xs transition-colors"
                  >
                    Use Original
                  </button>
                </div>
              </div>
            )}
            {composeError && (
              <p className="text-xs text-red-400">{composeError}</p>
            )}
          </div>
        )}

        {/* Live Blogs View */}
        {viewMode === 'blogs' && <BlogDashboard embedded />}

        {/* Pipeline Views (List + Calendar) */}
        {(viewMode === 'list' || viewMode === 'calendar') && (
          <>
            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
                {error}
                <button onClick={() => setError('')} className="ml-2 text-red-400 hover:text-red-200">&times;</button>
              </div>
            )}

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <ContentCalendar
                content={content}
                onSelectDate={(date) => setCalendarDate(date === calendarDate ? null : date)}
                selectedDate={calendarDate}
              />
            )}

            {/* Filters */}
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
                {statuses.map(s => {
                  const count = s === 'all' ? nonArchived.length : content.filter(c => c.status === s).length;
                  return (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        statusFilter === s
                          ? 'bg-gray-700 text-gray-100'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {s === 'all' ? `All (${count})` : `${s} (${count})`}
                    </button>
                  );
                })}
              </div>
              {activeFormatFilters.length > 2 && (
                <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
                  {activeFormatFilters.map(f => (
                    <button
                      key={f.key}
                      onClick={() => setFormatFilter(f.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        formatFilter === f.key
                          ? 'bg-gray-700 text-gray-100'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
              {calendarDate && (
                <button
                  onClick={() => setCalendarDate(null)}
                  className="px-3 py-1.5 bg-blue-600/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-600/30 transition-colors"
                >
                  Showing {calendarDate} &times;
                </button>
              )}
            </div>

            {/* Content List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No content found. Run <code className="text-yellow-400">/draft-content "topic"</code> to create a blog draft.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(item => (
                  <div key={item.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start gap-4">
                      {/* Thumbnail / Video Preview */}
                      {(item.selectedImageUrl || item.videoUrl || item.format === 'chess_clip') && (
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-900">
                          {item.videoUrl ? (
                            <video
                              src={item.videoUrl}
                              poster={item.format === 'chess_clip' ? '/assets/images/GoldKing.png' : undefined}
                              className="w-full h-full object-cover"
                              muted
                              loop
                              playsInline
                              onMouseEnter={(e) => e.target.play()}
                              onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                            />
                          ) : (
                            <img
                              src={item.selectedImageUrl || '/assets/images/GoldKing.png'}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {/* Status badge */}
                          <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[item.status] || 'bg-gray-700 text-gray-400'}`}>
                            {item.status}
                          </span>
                          {item.format && (
                            <span className={`text-xs px-2 py-0.5 rounded ${FORMAT_COLORS[item.format] || 'bg-gray-700 text-gray-400'}`}>
                              {FORMAT_LABELS[item.format] || item.format}
                            </span>
                          )}
                          {item.repurposedFrom && (
                            <span className="text-xs px-2 py-0.5 rounded bg-teal-600/20 text-teal-400">repurposed</span>
                          )}
                          {/* Video pipeline status */}
                          {item.videoStatus && (
                            <span className="text-xs px-2 py-0.5 rounded bg-purple-600/20 text-purple-400">
                              🎬 {VIDEO_STATUS_LABELS[item.videoStatus] || item.videoStatus}
                            </span>
                          )}
                        </div>
                        <h4 className="text-gray-200 font-medium text-sm">
                          {item.title || item.topic || '(untitled)'}
                        </h4>
                        {item.body && (
                          <p className="text-gray-400 text-xs mt-1 line-clamp-2">{item.body.slice(0, 200)}</p>
                        )}
                        <div className="flex gap-3 mt-2 text-xs text-gray-500">
                          <span>{formatDate(item.createdAt)}</span>
                          {item.tags && item.tags.length > 0 && (
                            <span>{item.tags.join(', ')}</span>
                          )}
                          {item.publishedTo && item.publishedTo.length > 0 && (
                            <span>Published to: {item.publishedTo.join(', ')}</span>
                          )}
                        </div>
                        {/* Video pipeline progress */}
                        {item.videoQueueId && (
                          <div className="flex gap-3 mt-2 text-xs">
                            <span className={item.voiceoverUrl ? 'text-teal-400' : 'text-gray-600'}>
                              {item.voiceoverUrl ? '✓' : '○'} Voiceover
                            </span>
                            <span className={item.avatarVideoUrl ? 'text-purple-400' : 'text-gray-600'}>
                              {item.avatarVideoUrl ? '✓' : '○'} Avatar
                            </span>
                            <span className={item.brollMixedUrl ? 'text-green-400' : 'text-gray-600'}>
                              {item.brollMixedUrl ? '✓' : '○'} B-Roll
                            </span>
                          </div>
                        )}
                        {/* Inline media players */}
                        {item.videoQueueId && (item.voiceoverUrl || item.avatarVideoUrl || item.brollMixedUrl) && (
                          <div className="flex flex-wrap gap-3 mt-3">
                            {/* Voiceover audio player */}
                            {item.voiceoverUrl && (
                              <div className="flex items-center gap-2 bg-teal-900/30 border border-teal-700/50 rounded-lg px-2 py-1">
                                <span className="text-xs text-teal-400">🎙️</span>
                                <audio
                                  src={item.voiceoverUrl}
                                  controls
                                  className="h-6 w-32"
                                  style={{ filter: 'sepia(20%) saturate(70%) hue-rotate(140deg)' }}
                                />
                              </div>
                            )}
                            {/* Avatar video player (only if no B-roll yet) */}
                            {item.avatarVideoUrl && !item.brollMixedUrl && (
                              <div className="flex items-center gap-2 bg-purple-900/30 border border-purple-700/50 rounded-lg px-2 py-1">
                                <span className="text-xs text-purple-400">🎬</span>
                                <video
                                  src={item.avatarVideoUrl}
                                  controls
                                  className="h-16 w-28 rounded"
                                />
                              </div>
                            )}
                            {/* B-roll mixed video player */}
                            {item.brollMixedUrl && (
                              <div className="flex items-center gap-2 bg-green-900/30 border border-green-700/50 rounded-lg px-2 py-1">
                                <div className="flex flex-col">
                                  <span className="text-xs text-green-400 mb-1">Final Video</span>
                                  <video
                                    src={item.brollMixedUrl}
                                    controls
                                    className="h-16 w-28 rounded"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                        <button
                          onClick={() => navigate(`/admin/content/editor/${item.id}`)}
                          className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded text-xs transition-colors"
                        >
                          Edit
                        </button>

                        {/* Tweet/Thread: Publish button */}
                        {item.status === 'draft' && (item.format === 'tweet' || item.format === 'tweet_thread') && (
                          <button
                            onClick={() => handleApproveAndPost(item.id)}
                            disabled={actionLoading === item.id}
                            className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 text-white px-3 py-1 rounded text-xs transition-colors"
                          >
                            {actionLoading === item.id ? 'Publishing...' : 'Publish'}
                          </button>
                        )}
                        {/* Blog/IG: Publish button (skipping approve) */}
                        {item.status === 'draft' && (item.format === 'blog' || item.format === 'ig') && (
                          <button
                            onClick={() => handlePublish(item.id)}
                            disabled={actionLoading === item.id}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-3 py-1 rounded text-xs transition-colors"
                          >
                            {actionLoading === item.id ? 'Publishing...' : 'Publish'}
                          </button>
                        )}

                        {item.status === 'published' && item.format === 'blog' && (
                          <div className="flex gap-1">
                            {[
                              { fmt: 'tweet_thread', label: 'Tweets' },
                              { fmt: 'yt_script_short', label: 'Short' },
                              { fmt: 'yt_script_long', label: 'Long' },
                              { fmt: 'ig', label: 'IG' }
                            ].map(({ fmt, label }) => (
                              <button
                                key={fmt}
                                onClick={() => handleRepurpose(item.id, fmt)}
                                disabled={actionLoading === item.id}
                                className="bg-teal-600/20 hover:bg-teal-600/40 text-teal-400 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Send to Video Queue button for YT scripts not yet in queue */}
                        {!item.videoQueueId && (item.format === 'yt_script' || item.format === 'yt_script_short' || item.format === 'yt_script_long') && (
                          <button
                            onClick={() => handleSendToQueue(item.id)}
                            disabled={sendingToQueue[item.id]}
                            className="bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                          >
                            {sendingToQueue[item.id] ? 'Sending...' : '→ Video Queue'}
                          </button>
                        )}

                        {/* Video pipeline action buttons */}
                        {item.videoQueueId && (
                          <>
                            {/* Queued: AI Voice button + Upload file input */}
                            {item.videoStatus === 'queued' && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleGenerateVoice(item.videoQueueId)}
                                  disabled={generatingVoice[item.videoQueueId]}
                                  className="bg-teal-600/20 hover:bg-teal-600/40 text-teal-400 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                                >
                                  {generatingVoice[item.videoQueueId] ? '...' : 'AI Voice'}
                                </button>
                                <label className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-2 py-1 rounded text-xs transition-colors cursor-pointer">
                                  {uploadingSelf[item.videoQueueId] ? '...' : 'Upload'}
                                  <input
                                    type="file"
                                    accept="video/*"
                                    className="hidden"
                                    onChange={(e) => handleSelfRecordingUpload(e, item.videoQueueId)}
                                    disabled={uploadingSelf[item.videoQueueId]}
                                  />
                                </label>
                              </div>
                            )}
                            {/* Voice Ready: Generate Video button */}
                            {item.videoStatus === 'voice_ready' && (
                              <button
                                onClick={() => handleGenerateVideo(item.videoQueueId)}
                                disabled={generatingVideo[item.videoQueueId]}
                                className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                              >
                                {generatingVideo[item.videoQueueId] ? 'Generating...' : 'Gen Video'}
                              </button>
                            )}
                            {/* Rendering: Spinner */}
                            {item.videoStatus === 'rendering' && (
                              <span className="text-xs text-yellow-400 flex items-center gap-1">
                                <span className="animate-spin">⟳</span> Rendering...
                              </span>
                            )}
                            {/* Rendered or B-Roll Mixed: B-Roll button */}
                            {(item.videoStatus === 'rendered' || item.videoStatus === 'broll_mixed') && (
                              <button
                                onClick={() => handleCopyBrollCommand(item.videoQueueId)}
                                className={`px-2 py-1 rounded text-xs transition-colors ${
                                  copiedBrollId === item.videoQueueId
                                    ? 'bg-green-600 text-white'
                                    : 'bg-green-600/20 hover:bg-green-600/40 text-green-400'
                                }`}
                              >
                                {copiedBrollId === item.videoQueueId ? 'Copied!' : 'B-Roll'}
                              </button>
                            )}
                          </>
                        )}

                        {/* Trash (Archive) for all non-archived */}
                        {item.status !== 'archived' && (
                          <button
                            onClick={() => handleArchive(item)}
                            disabled={actionLoading === item.id}
                            className="bg-gray-600/20 hover:bg-gray-600/40 text-gray-400 px-2 py-1 rounded text-sm transition-colors disabled:opacity-50"
                            title="Move to trash"
                          >
                            🗑️
                          </button>
                        )}

                        {/* Return (Unarchive) for archived items */}
                        {item.status === 'archived' && (
                          <button
                            onClick={() => handleUnarchive(item)}
                            disabled={actionLoading === item.id}
                            className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
                            title="Restore to drafts"
                          >
                            ↩ Return
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* New Content Modal */}
        {showNewContentModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-100">Create New Content</h2>
                <button
                  onClick={() => setShowNewContentModal(false)}
                  className="text-gray-400 hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Format</label>
                  <select
                    value={newContentFormat}
                    onChange={(e) => setNewContentFormat(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="blog">Blog Post</option>
                    <option value="tweet">Tweet</option>
                    <option value="tweet_thread">Tweet Thread</option>
                    <option value="yt_script">YouTube Script</option>
                    <option value="ig">Instagram Caption</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Topic / Title</label>
                  <input
                    type="text"
                    value={newContentTopic}
                    onChange={(e) => setNewContentTopic(e.target.value)}
                    placeholder="Enter topic or title..."
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Body</label>
                  <textarea
                    value={newContentBody}
                    onChange={(e) => setNewContentBody(e.target.value)}
                    placeholder="Write your content..."
                    rows={8}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-gray-200 text-sm focus:ring-1 focus:ring-blue-500 resize-y"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => setShowNewContentModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-gray-200 text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateManualContent}
                  disabled={creatingContent || !newContentTopic.trim() || !newContentBody.trim()}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  {creatingContent ? 'Creating...' : 'Create Draft'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminAuth>
  );
};

export default ContentPipelinePage;
